import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Avatar, Badge } from "antd";
import { CalendarOutlined } from "@ant-design/icons";

import DraggableWindow from "../communications/DraggableWindow";
import activitiesActions, {
  fetchUndoneActivityCount,
} from "../actions/activities";
import { detectRecordFromPath } from "../common/recordTypes";

import ActivityWidgetCreate from "./ActivityWidgetCreate";
import ActivityWidgetList from "./ActivityWidgetList";
import ActivityWidgetView from "./ActivityWidgetView";
import { WIDGET_TABS } from "./constants";

import styles from "./ActivitiesWidget.module.scss";

const ActivitiesWidget = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const userId = useSelector((state) => state.user?.data?.id);
  const undoneCount = useSelector(
    (state) => state.activities?.undoneCount ?? 0
  );
  const { isExpanded, activeTab, selectedActivityId } = useSelector(
    (state) => state.activities?.widget || {}
  );
  const record = useMemo(() => detectRecordFromPath(pathname), [pathname]);

  useEffect(() => {
    if (!userId) return;

    dispatch(fetchUndoneActivityCount());
  }, [dispatch, userId]);

  const toggleExpanded = () => {
    dispatch(activitiesActions.setWidgetExpanded(!isExpanded));
  };

  const handleMinimize = () => {
    dispatch(activitiesActions.setWidgetExpanded(false));
  };

  const handleBackFromView = () => {
    dispatch(activitiesActions.setSelectedActivityId(null));
  };

  const handleBackFromCreate = () => {
    dispatch(activitiesActions.setWidgetActiveTab(WIDGET_TABS.LIST));
  };

  const handleOpenCreate = () => {
    dispatch(activitiesActions.setWidgetActiveTab(WIDGET_TABS.CREATE));
  };

  return (
    <>
      <Badge
        count={undoneCount}
        overflowCount={99}
        rootClassName={styles.badge}
        classNames={{ indicator: styles.count }}
      >
        <Avatar
          className={`${styles.triggerIcon} ${
            isExpanded ? styles.expanded : styles.collapsed
          }`}
          shape="square"
          size={34}
          icon={<CalendarOutlined />}
          onClick={toggleExpanded}
        />
      </Badge>

      <DraggableWindow
        title="Activities"
        isCollapsed={!isExpanded}
        onMinimize={handleMinimize}
        queueLength={0}
        currentIndex={0}
        defaultPosition={{ x: window.innerWidth - 460, y: 55 }}
      >
        <div className={styles.body}>
          {selectedActivityId ? (
            <ActivityWidgetView
              id={selectedActivityId}
              onBack={handleBackFromView}
            />
          ) : activeTab === WIDGET_TABS.CREATE ? (
            <ActivityWidgetCreate
              record={record}
              onBack={handleBackFromCreate}
            />
          ) : (
            <ActivityWidgetList onCreate={handleOpenCreate} />
          )}
        </div>
      </DraggableWindow>
    </>
  );
};

export default ActivitiesWidget;
